import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import React, { useEffect, useRef, useState } from "react";
const TIME_BEFORE_AUTO_SCROLL = 3500;
const HorizontalInfos = (props) => {
    const { id, dynamicList = [], ...others } = props;
    const [itemActive, setItemActive] = useState(dynamicList[0]);
    const [isAutoScrolling, setIsAutoScrolling] = useState(false);
    const intervalRef = useRef(null);
    const containerRef = useRef(null);
    const autoScrollDisabledRef = useRef(false);
    const stopAutoScroll = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        setIsAutoScrolling(false);
    };
    const startAutoScroll = () => {
        if (autoScrollDisabledRef.current)
            return;
        if (intervalRef.current)
            return;
        setIsAutoScrolling(true);
        intervalRef.current = setInterval(() => {
            setItemActive((prev) => {
                const currentIndex = dynamicList.indexOf(prev);
                return dynamicList[(currentIndex + 1) % dynamicList.length];
            });
        }, TIME_BEFORE_AUTO_SCROLL);
    };
    // Start auto-scroll when the component is in view
    useEffect(() => {
        if (dynamicList.length <= 1 || !containerRef.current)
            return;
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                startAutoScroll();
            }
            else {
                stopAutoScroll();
            }
        }, { threshold: 0.2 });
        observer.observe(containerRef.current);
        return () => {
            observer.disconnect();
            stopAutoScroll();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dynamicList]);
    const handleItemClick = (item) => {
        stopAutoScroll();
        autoScrollDisabledRef.current = true;
        setItemActive(item);
    };
    return (_jsxs(Stack, { spacing: 4, ref: containerRef, children: [_jsx(Grid, { container: true, ...others, children: dynamicList?.map((item) => (_jsx(Grid, { size: { xs: 12, sm: 6, md: 4, lg: 2 }, role: "button", onClick: () => handleItemClick(item), sx: {
                        position: "relative",
                        cursor: "pointer",
                        transition: "opacity 300ms ease, border-color 300ms ease",
                        borderBottom: "1px solid",
                        borderColor: "border.soft",
                        "@keyframes horizontalInfosProgress": {
                            from: {
                                transform: "scaleX(0)",
                            },
                            to: {
                                transform: "scaleX(1)",
                            },
                        },
                        ...(itemActive === item
                            ? {
                                "&::after": {
                                    content: '""',
                                    position: "absolute",
                                    left: 0,
                                    bottom: -1,
                                    width: "100%",
                                    height: 3,
                                    backgroundColor: "border.active",
                                    transformOrigin: "left",
                                    animation: !isAutoScrolling || autoScrollDisabledRef.current
                                        ? "none"
                                        : `horizontalInfosProgress ${TIME_BEFORE_AUTO_SCROLL}ms linear forwards`,
                                    animationDelay: "0ms",
                                    animationIterationCount: 1,
                                    transform: !isAutoScrolling || autoScrollDisabledRef.current
                                        ? "scaleX(1)"
                                        : "scaleX(0)",
                                },
                            }
                            : {
                                "&::after": {
                                    content: '""',
                                    position: "absolute",
                                    left: 0,
                                    bottom: -1,
                                    width: "100%",
                                    height: 3,
                                    backgroundColor: "border.active",
                                    transformOrigin: "left",
                                    animation: "none",
                                    transform: "scaleX(0)",
                                },
                            }),
                        ...(itemActive === item
                            ? {
                                opacity: 1,
                            }
                            : {
                                opacity: 0.7,
                            }),
                    }, children: _jsxs(Stack, { spacing: 2, sx: {
                            paddingY: 3.5,
                            paddingX: 2.75,
                        }, children: [item.title && (_jsx(Typography, { variant: "subtitle1", component: "p", sx: { color: "primary.contrastText", fontSize: "32px" }, children: item.title.split("+").map((part, i, arr) => (_jsxs(React.Fragment, { children: [part, i < arr.length - 1 && (_jsx("span", { style: {
                                                color: item.color ?? "inherit",
                                            }, children: "+" }))] }, i))) })), item.subtitle && (_jsx(Typography, { variant: "body1", sx: { color: "primary.contrastText" }, children: item.subtitle }))] }) }, item.title + (item.subtitle ?? "")))) }, id), _jsx(Typography, { variant: "subtitle1", component: "p", sx: {
                    color: "primary.contrastText",
                    animation: "descriptionReveal 320ms ease",
                    "@keyframes descriptionReveal": {
                        from: {
                            opacity: 0,
                            transform: "translateY(6px)",
                        },
                        to: {
                            opacity: 1,
                            transform: "translateY(0)",
                        },
                    },
                }, children: itemActive?.description ?? "" })] }));
};
export default HorizontalInfos;
